import RuleAccountFilter, {
  EMPTY_RULE_ACCOUNT_FILTER,
} from "@app/components/rule-account-filter";
import type { RuleAccountFilterValue } from "@app/components/rule-account-filter";
import type { AccountOption } from "@app/components/account-combobox";
import { MATCH_KINDS } from "@app/lib/match-kinds";
import { useState } from "react";

type Suggestion = {
  readonly regex: string;
  readonly matchKind: string;
  readonly additionalCount: number;
  readonly totalCount: number;
};

// Shown right after a bulk attribution: offers to turn the derived pattern
// into a standing rule. Offered even when the selection already caught
// everything (additionalCount === 0) — the rule then keeps future imports
// attributed. The rule can optionally be limited to specific source/destination
// accounts before it's created.
export default function RuleSuggestionDialog({
  stakeholderName,
  suggestion,
  accounts,
  prefillSourceIds,
  saving,
  onConfirm,
  onDismiss,
}: {
  stakeholderName: string;
  suggestion: Suggestion;
  accounts: ReadonlyArray<AccountOption>;
  prefillSourceIds: ReadonlyArray<string>;
  saving: boolean;
  onConfirm: (
    filter: RuleAccountFilterValue,
    regex: string,
    matchKind: string
  ) => void;
  onDismiss: () => void;
}) {
  const { additionalCount } = suggestion;
  const [filter, setFilter] = useState<RuleAccountFilterValue>(
    EMPTY_RULE_ACCOUNT_FILTER
  );
  // The suggested pattern is editable before the rule is created — tighten or
  // loosen it, or switch the operator (Contains/Starts with/…). Seeded from the
  // server's suggestion.
  const [regex, setRegex] = useState(suggestion.regex);
  const [matchKind, setMatchKind] = useState(suggestion.matchKind);
  const isRegex = matchKind === "REGEX";
  const edited =
    regex !== suggestion.regex || matchKind !== suggestion.matchKind;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center"
      onClick={onDismiss}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[460px] p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-sm font-semibold text-gray-800 mb-1">
          Create a rule for {stakeholderName}?
        </h2>
        <p className="text-sm text-gray-600 mb-3">
          {additionalCount > 0 ? (
            <>
              Found{" "}
              <span className="font-semibold">
                {additionalCount.toLocaleString()}
              </span>{" "}
              more transaction
              {additionalCount === 1 ? "" : "s"} matching this pattern.
              Attribute them all and create a rule so future imports stay
              attributed?
            </>
          ) : (
            <>
              Every matching transaction is already attributed. Create a
              standing rule anyway so future imports are attributed
              automatically?
            </>
          )}
        </p>
        <div className="mb-3 space-y-1">
          <label className="text-xs text-gray-500">Match pattern</label>
          <div className="flex items-center gap-2">
            <select
              value={matchKind}
              onChange={(e) => setMatchKind(e.target.value)}
              className="rounded border px-2 py-1.5 text-sm bg-white shrink-0"
            >
              {MATCH_KINDS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <input
              value={regex}
              onChange={(e) => setRegex(e.target.value)}
              placeholder={isRegex ? "^ACME.*" : "ACME"}
              className="flex-1 min-w-0 rounded border px-2 py-1.5 font-mono text-xs"
            />
          </div>
          <p className="text-[11px] text-gray-400">
            {MATCH_KINDS.find((m) => m.value === matchKind)?.hint}
            {edited && additionalCount > 0
              ? " — count above reflects the originally suggested pattern"
              : ""}
          </p>
        </div>
        <div className="mb-4">
          <RuleAccountFilter
            accounts={accounts}
            value={filter}
            onChange={setFilter}
            showDestination
            prefillSourceIds={prefillSourceIds}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={saving || regex.trim() === ""}
            onClick={() => onConfirm(filter, regex, matchKind)}
            className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
          >
            {saving ? "Creating…" : "Create rule"}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800"
          >
            No thanks
          </button>
        </div>
      </div>
    </div>
  );
}
